import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  Building2,
  Loader2,
  Plus,
  Sparkles,
  Table2,
  Users,
} from 'lucide-react';
import { useProspecting } from '../context/ProspectingContext';
import { useToast } from '../context/ToastContext';
import {
  archiveProspectingSavedList,
  loadProspectingSavedLists,
} from '../components/Prospecting/prospectingApi';
import { shouldAutoStartGuided } from './prospectingLanding';

function SearchCard({ search, onOpen }) {
  const Icon = search.type === 'companies' ? Building2 : Users;
  const filterCount = search.filters.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-xl border border-gray-150 dark:border-gray-700/80 bg-white dark:bg-gray-800/50 p-4 transition-all hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </div>
        <span className="font-medium text-gray-900 dark:text-white truncate">{search.name}</span>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {filterCount === 0 ? 'No filters yet' : `${filterCount} filter${filterCount === 1 ? '' : 's'} applied`}
      </div>
    </button>
  );
}

function SavedListCard({ list, onOpen, onArchive, archiving }) {
  const Icon = list.entity_type === 'company' ? Building2 : Users;
  const recordCount = Number(
    list.record_count ?? list.records_count ?? list.result_count ?? list.count ?? 0
  );

  return (
    <div className="group relative rounded-xl border border-gray-150 dark:border-gray-700/80 bg-white dark:bg-gray-800/50 transition-all hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm hover:-translate-y-0.5">
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-4 pr-12"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white truncate">{list.name}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {Number.isFinite(recordCount) ? `${recordCount} record${recordCount === 1 ? '' : 's'}` : 'Saved list'}
        </div>
      </button>
      <button
        type="button"
        onClick={onArchive}
        disabled={archiving}
        title="Archive saved list"
        aria-label={`Archive ${list.name}`}
        className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-gray-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all hover:border-amber-200 hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-500 dark:hover:border-amber-800 dark:hover:bg-amber-900/20 dark:hover:text-amber-300"
      >
        {archiving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Archive className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
}

function ListsLoadingState() {
  return (
    <div className="flex-1 flex items-center justify-center px-6">
      <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your prospecting workspace
      </div>
    </div>
  );
}

function ProspectingHome({
  searches,
  searchOrder,
  savedLists,
  savedListsLoading,
  savedListsError,
  archivingListId,
  onOpenSearch,
  onOpenSavedList,
  onArchiveSavedList,
  onCreateGuided,
}) {
  return (
    <div className="flex-1 overflow-y-auto px-6 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Your prospecting workspace</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Continue from previous searches or jump into a saved list.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCreateGuided}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> Start new search
            </button>
          </div>
        </div>
        {savedListsError && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
            {savedListsError}
          </div>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                Searches
              </h2>
              <span className="text-xs text-gray-400 dark:text-gray-500">{searchOrder.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {searchOrder.map((id) => {
                const search = searches[id];
                if (!search) return null;
                return (
                  <SearchCard
                    key={id}
                    search={search}
                    onOpen={() => onOpenSearch(id)}
                  />
                );
              })}
              <button
                type="button"
                onClick={onCreateGuided}
                className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-transparent p-4 flex items-center justify-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-brand-300 dark:hover:border-brand-700 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                <Plus className="w-4 h-4" /> New search
              </button>
            </div>
            {searchOrder.length === 0 && (
              <div className="mt-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                No searches yet. Start a guided search to build your first one.
              </div>
            )}
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                <Table2 className="w-3.5 h-3.5" />
                Saved Lists
              </h2>
              <span className="text-xs text-gray-400 dark:text-gray-500">{savedLists.length}</span>
            </div>
            {savedListsLoading ? (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3 text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading saved lists
              </div>
            ) : savedLists.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                No saved lists yet. Save results from a search to build your first list.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {savedLists.map((list) => (
                  <SavedListCard
                    key={list.id}
                    list={list}
                    onOpen={() => onOpenSavedList(list.id)}
                    onArchive={() => onArchiveSavedList(list)}
                    archiving={archivingListId === list.id}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function Prospecting() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { searches, searchOrder } = useProspecting();
  const [savedLists, setSavedLists] = React.useState([]);
  const [savedListsLoading, setSavedListsLoading] = React.useState(true);
  const [savedListsError, setSavedListsError] = React.useState('');
  const [archivingListId, setArchivingListId] = React.useState(null);

  const handleCreateGuided = () => {
    navigate('/prospecting/new');
  };

  const handleOpenSearch = (id) => {
    navigate(`/prospecting/${id}`);
  };

  const handleOpenSavedList = (listId) => {
    navigate(`/prospecting/lists/${listId}`);
  };

  const handleArchiveSavedList = async (list) => {
    if (!list?.id || archivingListId) return;
    const name = String(list.name || 'this saved list');
    const confirmArchive = window.confirm(`Archive "${name}"? It will be removed from your main lists.`);
    if (!confirmArchive) return;

    setArchivingListId(list.id);
    setSavedListsError('');
    try {
      await archiveProspectingSavedList(list.id);
      setSavedLists((current) => current.filter((entry) => entry.id !== list.id));
      showSuccess('Saved list archived');
    } catch (error) {
      showError(error?.message || 'Could not archive saved list');
    } finally {
      setArchivingListId(null);
    }
  };

  React.useEffect(() => {
    const controller = new AbortController();
    setSavedListsLoading(true);
    setSavedListsError('');

    loadProspectingSavedLists({ signal: controller.signal })
      .then((lists) => {
        setSavedLists(Array.isArray(lists) ? lists : []);
        setSavedListsLoading(false);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setSavedListsLoading(false);
        setSavedListsError(error?.message || 'Could not load saved lists');
      });

    return () => controller.abort();
  }, []);

  React.useEffect(() => {
    if (shouldAutoStartGuided({
      savedListsLoading,
      savedListsError,
      searchCount: searchOrder.length,
      savedListCount: savedLists.length,
    })) {
      navigate('/prospecting/new', { replace: true });
    }
  }, [navigate, savedLists.length, savedListsError, savedListsLoading, searchOrder.length]);

  if (savedListsLoading && searchOrder.length === 0 && savedLists.length === 0) {
    return <div className="h-screen flex flex-col bg-white dark:bg-gray-900"><ListsLoadingState /></div>;
  }

  if (shouldAutoStartGuided({
    savedListsLoading,
    savedListsError,
    searchCount: searchOrder.length,
    savedListCount: savedLists.length,
  })) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      <ProspectingHome
        searches={searches}
        searchOrder={searchOrder}
        savedLists={savedLists}
        savedListsLoading={savedListsLoading}
        savedListsError={savedListsError}
        archivingListId={archivingListId}
        onOpenSearch={handleOpenSearch}
        onOpenSavedList={handleOpenSavedList}
        onArchiveSavedList={handleArchiveSavedList}
        onCreateGuided={handleCreateGuided}
      />
    </div>
  );
}
